<template>
  <BaseModule
    v-bind="$props"
    class="container maps"
    data-module="maps"
  >
    <div
      :ref="mapsId"
      class="content"
    >
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
      </header>
      <l-map
        :key="zoom"
        :zoom="zoom"
        :center="centralMapFocusCoordinates"
      >
        <l-tile-layer
          :url="url"
          :attribution="attribution"
        />
        <l-marker :lat-lng="primaryCoordinates">
          <l-icon
            :icon-url="getMarkerIcon(primaryAddressMarkerColor)"
            :shadow-url="shadowUrlPath"
          />
          <l-tooltip
            :class="{ 'hidden-tooltip': !showTooltip(tooltipText) }"
            :options="{ permanent: true }"
          >
            <div>
              {{ tooltipText }}
            </div>
          </l-tooltip>
        </l-marker>
        <l-tile-layer
          :url="url"
          :attribution="attribution"
        />
        <l-marker
          v-for="(item, index) in secondaryAddresses"
          :key="index"
          :lat-lng="
            getCoordinates(
              item.address,
              item.useExactCoordinates,
              item.exactLat,
              item.exactLng,
            )
          "
        >
          <l-icon
            :icon-url="getMarkerIcon(item.addressMarkerColor)"
            :shadow-url="shadowUrlPath"
          />
          <l-tooltip
            :class="{ 'hidden-tooltip': !showTooltip(item.tooltipText) }"
            :options="{ permanent: true }"
          >
            <div>
              {{ item.tooltipText }}
            </div>
          </l-tooltip>
        </l-marker>
      </l-map>
    </div>
  </BaseModule>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import { LMap, LTileLayer, LMarker, LTooltip, LIcon } from 'vue2-leaflet';

import base from '@/mixins/base';
import uniqueId from '@/helpers/uniqueId';
import BaseModule from './generic/BaseModule.vue';

const shadowUrlPath = require('@/assets/shadow.png');

export default {
  name: 'Maps',
  components: {
    BaseModule,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LIcon,
  },
  mixins: [base],
  props: {
    ...BaseModule.props,
    zoom: {
      type: Number,
      default: 10,
    },
    primaryAddress: {
      type: Object,
      default() {
        return {
          // eslint-disable-next-line id-length
          y: -13.14,
          // eslint-disable-next-line id-length
          x: -48.92,
        };
      },
    },
    primaryAddressMarkerColor: {
      type: String,
      default: '#3E9AA6',
    },
    tooltipText: {
      type: String,
      default: '',
    },
    exactLat: {
      type: String,
      default: '',
    },
    exactLng: {
      type: String,
      default: '',
    },
    useExactCoordinates: {
      type: Boolean,
      default: false,
    },
    secondaryAddresses: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      mapsId: null,
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      shadowUrlPath,
      iconSize: 30,
    };
  },
  computed: {
    centralMapFocusCoordinates() {
      return this.primaryCoordinates;
    },
    primaryCoordinates() {
      return this.getCoordinates(
        this.primaryAddress,
        this.useExactCoordinates,
        this.exactLat,
        this.exactLng,
      );
    },
  },
  watch: {
    secondaryAddresses: {
      handler() {
        this.handleEmptyTooltips();
      },
      deep: true,
    },
    zoom() {
      this.handleEmptyTooltips();
    },
    tooltipText() {
      this.handleEmptyTooltips();
    },
  },
  mounted() {
    this.mapsId = uniqueId();
    this.handleEmptyTooltips();
    this.dispatchResize();
  },
  methods: {
    getCoordinates(address, useExactCoordinates, exactLat, exactLng) {
      return (
        this.getManualInputCoordinates(
          useExactCoordinates,
          exactLat,
          exactLng,
        ) || this.getAutofillAddressCoordinates(address, useExactCoordinates)
      );
    },
    getAutofillAddressCoordinates(address, useExactCoordinates) {
      return address && !useExactCoordinates ? [address.y, address.x] : [0, 0];
    },
    getManualInputCoordinates(useExactCoordinates, exactLat, exactLng) {
      if (
        useExactCoordinates &&
        !Number.isNaN(parseFloat(exactLat)) &&
        !Number.isNaN(parseFloat(exactLng))
      ) {
        return [exactLat, exactLng];
      }

      return null;
    },
    showTooltip(text) {
      return text !== undefined && text.trim() !== '';
    },
    getMarkerIcon(color) {
      const fill = color ? color.replace('#', '%23') : '%23466AF0';
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='42' height='70' viewBox='0 0 42 70' fill='none'%3E%3Cpath d='M31.5 23.3333C31.5 20.1129 30.4746 17.3633 28.4239 15.0846C26.373 12.806 23.8985 11.6667 21 11.6667C18.1015 11.6667 15.627 12.806 13.5762 15.0846C11.5254 17.3633 10.5 20.1129 10.5 23.3333C10.5 26.5538 11.5254 29.3034 13.5762 31.5821C15.627 33.8607 18.1015 35 21 35C23.8985 35 26.373 33.8607 28.4239 31.5821C30.4746 29.3034 31.5 26.5538 31.5 23.3333ZM42 23.3333C42 26.645 41.5489 29.3641 40.6465 31.4909L25.7168 66.7643C25.2793 67.767 24.6299 68.5568 23.7686 69.1341C22.9073 69.7114 21.9844 70 21 70C20.0156 70 19.0927 69.7114 18.2314 69.1341C17.3701 68.5568 16.7344 67.767 16.3242 66.7643L1.35352 31.4909C0.451172 29.3641 0 26.645 0 23.3333C0 16.8924 2.05078 11.3932 6.15235 6.83594C10.2539 2.27864 15.2031 0 21 0C26.7969 0 31.7461 2.27864 35.8476 6.83594C39.9492 11.3932 42 16.8924 42 23.3333Z' fill='${fill}'/%3E%3C/svg%3E`;
    },
    handleEmptyTooltips() {
      setTimeout(() => {
        [].slice
          .call(this.$refs[this.mapsId].querySelectorAll('.leaflet-tooltip'))
          .forEach((tp) => {
            const tolltip = tp;
            tolltip.style.display = 'block';
            if (tolltip.querySelector('.hidden-tooltip')) {
              tolltip.style.display = 'none';
            }
          });
      }, 200);
    },
    dispatchResize() {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 250);
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'primaryAddress',
            label: 'Endereço Principal',
            fields: [
              {
                name: 'useExactCoordinates',
                type: 'Checkbox',
                label: 'Inserir coordenadas exatas',
              },
              {
                name: 'primaryAddress',
                type: 'SelectGeo',
                label: 'Buscar um endereço',
                isEnabled({ useExactCoordinates }) {
                  return !useExactCoordinates;
                },
              },
              {
                name: 'exactLat',
                type: 'ShortText',
                label: 'Latitude',
                mask: {
                  mask: Number,
                  min: -90,
                  max: 90,
                  radix: '.',
                  scale: 100,
                },
                helpText:
                  'Não sabe as coordenadas exatas? <br> Consulte <a href="https://www.mapsdirections.info/pt/coordenadas-gps.html" target="_blank">aqui</a>',
                maxlength: 100,
                isEnabled({ useExactCoordinates }) {
                  return useExactCoordinates;
                },
              },
              {
                name: 'exactLng',
                type: 'ShortText',
                label: 'Longitude',
                mask: {
                  mask: Number,
                  min: -180,
                  max: 180,
                  radix: '.',
                  scale: 100,
                },
                maxlength: 100,
                isEnabled({ useExactCoordinates }) {
                  return useExactCoordinates;
                },
              },
              {
                name: 'primaryAddressMarkerColor',
                type: 'ColorPicker',
                label: 'Cor do marcador',
                default: '#3E9AA6',
              },
              {
                name: 'tooltipText',
                type: 'ShortText',
                label: 'Texto do marcador',
                maxlength: 100,
              },
            ],
          },
          {
            name: 'secondaryAddresses',
            label: 'Endereços Secundários',
            fields: [
              {
                name: 'secondaryAddresses',
                type: 'List',
                itemLabel: 'Endereço secundário {index}',
                addItemLabel: 'Adicionar endereço',
                fields: [
                  {
                    name: 'address',
                    type: 'SelectGeo',
                    label: 'Buscar um endereço',
                    isEnabled() {
                      return !this.useExactCoordinates;
                    },
                  },
                  {
                    name: 'useExactCoordinates',
                    type: 'Checkbox',
                    label: 'Inserir coordenadas exatas',
                  },
                  {
                    name: 'exactLat',
                    type: 'ShortText',
                    label: 'Latitude',
                    mask: {
                      mask: Number,
                      min: -90,
                      max: 90,
                      radix: '.',
                      scale: 100,
                    },
                    helpText:
                      'Não sabe as coordenadas exatas? <br> Consulte <a href="https://www.mapsdirections.info/pt/coordenadas-gps.html" target="_blank">aqui</a>',
                    maxlength: 100,
                    isEnabled() {
                      return this.useExactCoordinates;
                    },
                  },
                  {
                    name: 'exactLng',
                    type: 'ShortText',
                    label: 'Longitude',
                    mask: {
                      mask: Number,
                      min: -180,
                      max: 180,
                      radix: '.',
                      scale: 100,
                    },
                    maxlength: 100,
                    isEnabled() {
                      return this.useExactCoordinates;
                    },
                  },
                  {
                    name: 'addressMarkerColor',
                    type: 'ColorPicker',
                    label: 'Cor do marcador',
                    default: '#3E9AA6',
                  },
                  {
                    name: 'tooltipText',
                    type: 'ShortText',
                    label: 'Texto do marcador',
                    maxlength: 100,
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'zoom',
                type: 'Range',
                label: 'Zoom inicial do Mapa',
                min: 10,
                max: 18,
                step: 1,
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['textColor', 'buttonColor', 'buttonTextColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.maps {
  width: 100%;
  padding: 0 42px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}

.vue2leaflet-map {
  height: 420px !important;
}

.hidden-tooltip .leaflet-tooltip {
  display: none;
}
</style>
